/** Route → required permission slug (first match wins). */
const ROUTE_RULES = [
  { prefix: "/pos", permission: "pos.view" },
  { prefix: "/inventory", permission: "inventory.view" },
  { prefix: "/masters", permission: "masters.view" },
  { prefix: "/accounts", permission: "accounts.view" },
  { prefix: "/reports", permission: "reports.view" },
  { prefix: "/admin", permission: "administration.view" },
] as const;

const DASHBOARD_PERMS = [
  "reports.view",
  "administration.view",
  "accounts.view",
  "masters.view",
  "inventory.view",
] as const;

export function can(permissions: string[] | undefined, slug: string): boolean {
  if (!slug) return true;
  return (permissions ?? []).includes(slug);
}

export function canAccessDashboard(permissions: string[] | undefined): boolean {
  return DASHBOARD_PERMS.some((p) => can(permissions, p));
}

export function canAccessPath(pathname: string, permissions: string[] | undefined): boolean {
  if (pathname === "/" || pathname === "") {
    return canAccessDashboard(permissions);
  }

  if (pathname === "/online" || pathname.startsWith("/online/")) {
    return can(permissions, "pos.view") || can(permissions, "masters.view");
  }

  for (const rule of ROUTE_RULES) {
    if (pathname === rule.prefix || pathname.startsWith(`${rule.prefix}/`)) {
      return can(permissions, rule.permission);
    }
  }

  return true;
}

/** Counter staff with POS only — no back-office modules. */
export function isMinimalStaff(permissions: string[] = []): boolean {
  const hasBackOffice = permissions.some(
    (p) =>
      p.startsWith("administration.")
      || p.startsWith("accounts.")
      || p.startsWith("reports.")
      || p.startsWith("masters.")
      || p.startsWith("inventory."),
  );
  if (hasBackOffice) return false;
  return permissions.some((p) => p.startsWith("pos."));
}

export function getDefaultRoute(permissions: string[] = []): string {
  if (canAccessDashboard(permissions)) return "/";
  if (permissions.some((p) => p.startsWith("pos."))) return "/pos";
  if (can(permissions, "inventory.view")) return "/inventory";
  if (can(permissions, "accounts.view")) return "/accounts/accounts";
  return "/login";
}

/** After login, only return to the prior page for front-line staff (not back-office). */
export function getPostLoginRoute(permissions: string[] = [], fromPath?: string): string {
  const home = getDefaultRoute(permissions);
  if (
    fromPath
    && fromPath !== "/login"
    && canAccessPath(fromPath, permissions)
    && !canAccessDashboard(permissions)
  ) {
    return fromPath;
  }
  return home;
}

export interface MenuItemWithPermission {
  permission?: string;
  accessCheck?: (permissions: string[]) => boolean;
  to?: string;
  [key: string]: unknown;
}

export function filterMenuItems<T extends MenuItemWithPermission>(items: T[], permissions: string[]): T[] {
  return items.filter((item) => {
    if (item.accessCheck) return item.accessCheck(permissions);
    return !item.permission || can(permissions, item.permission);
  });
}

export function filterMenus<T extends { items: MenuItemWithPermission[] }>(menus: T[], permissions: string[]): T[] {
  return menus
    .map((menu) => ({
      ...menu,
      items: filterMenuItems(menu.items, permissions),
    }))
    .filter((menu) => menu.items.length > 0);
}
